/*
 * Admin → «Marca de la app»: logo o imagen de fondo de la cabecera del menú lateral de la app (detrás de la foto, el
 * nombre y el correo), con vista previa del teléfono. La imagen se arrastra para colocarla y la rueda / el deslizador
 * cambia el tamaño. API: GET /org/marca-cajon, POST /admin/marca-cajon/imagen, PUT /admin/marca-cajon, DELETE.
 * Las cuentas de colocación son las MISMAS que en la app (app/lib/widgets/app_drawer.dart → _FondoCabecera):
 *   ancho = ancho_cabecera × escala/100 · alto por proporción · izquierda = (ancho_cabecera − ancho) × x/100 ·
 *   arriba = (alto_cabecera − alto) × y/100.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, ImagePlus, Loader2, Plus, RotateCcw, Save, Trash2, Move } from 'lucide-react';
import { api } from '../../lib/api';
import { useStore } from '../../store/useStore';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

const DEFECTO = { escala: 100, x: 50, y: 50, opacidad: 100, oscurecer: 30, desenfoque: 0, altura: 150, texto: 'auto', fondo: '#1e293b' };
// La vista previa se dibuja a TAMAÑO REAL (teléfono de 390 px, cajón de la app = min(82 %, 320 px)) y se reduce con
// CSS: así el encuadre (que depende del ancho y del alto en px) es exactamente el del teléfono.
const ANCHO_TEL = 390;
const ANCHO_CAJON = Math.round(Math.min(ANCHO_TEL * 0.82, 320));
const ESCALA_VISTA = 0.8;
const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

function luminancia(hex) {
  const n = parseInt((hex || '#000000').slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Texto claro u oscuro sobre la cabecera (mismo criterio que la app). */
export function textoClaro(aj, conImagen) {
  if (aj.texto === 'claro') return true;
  if (aj.texto === 'oscuro') return false;
  return conImagen ? aj.oscurecer >= 20 || luminancia(aj.fondo) < 0.5 : luminancia(aj.fondo) < 0.5;
}

function Deslizador({ etiqueta, valor, min, max, paso = 1, sufijo = '', onChange }) {
  return (
    <label className="block">
      <span className="flex justify-between text-sm"><span className="font-medium">{etiqueta}</span><span className="text-muted-foreground tabular-nums">{Math.round(valor)}{sufijo}</span></span>
      <input type="range" min={min} max={max} step={paso} value={valor} onChange={(e) => onChange(Number(e.target.value))} className="w-full mt-1.5 accent-[var(--accent)]" />
    </label>
  );
}

export default function PanelMarcaApp() {
  const user = useStore((s) => s.user);
  const [marca, setMarca] = useState(null);
  const [aj, setAj] = useState(DEFECTO);
  const [ocupado, setOcupado] = useState(false);
  const [sucio, setSucio] = useState(false);
  const archivo = useRef(null);
  const arrastre = useRef(null);
  const [galeria, setGaleria] = useState([]);
  const [eligiendo, setEligiendo] = useState(null);

  const cargarGaleria = async () => {
    const { data } = await api.get('/admin/marca-cajon/galeria');
    setGaleria(data.fondos || []);
  };
  const aplicar = (data) => {
    setMarca(data);
    setAj({ ...DEFECTO, ...(data.ajustes || {}) });
    setSucio(false);
  };
  const cargar = async () => {
    const { data } = await api.get('/org/marca-cajon');
    aplicar(data);
    await cargarGaleria();
  };
  useEffect(() => { cargar().catch(() => toast.error('No se pudo cargar la marca de la app')); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Elegir un fondo de la galería: pasa a la cabecera con sus ajustes sugeridos (se pueden cambiar después). */
  const usar = async (f) => {
    if (sucio && !window.confirm('Tienes cambios sin guardar en los ajustes. ¿Cambiar de fondo igualmente?')) return;
    setEligiendo(f.id);
    try {
      const { data } = await api.post('/admin/marca-cajon/usar', { id: f.id });
      aplicar(data);
      setGaleria((g) => g.map((x) => ({ ...x, activo: x.id === f.id })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo usar ese fondo');
    } finally {
      setEligiendo(null);
    }
  };

  const borrarDeGaleria = async (f) => {
    if (!window.confirm(`¿Borrar «${f.nombre}» de la galería?${f.activo ? ' Se seguirá viendo en la app hasta que elijas otro.' : ''}`)) return;
    try {
      await api.delete(`/admin/marca-cajon/galeria/${f.id}`);
      setGaleria((g) => g.filter((x) => x.id !== f.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const cambiar = (k, v) => { setAj((a) => ({ ...a, [k]: v })); setSucio(true); };

  const subir = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    setOcupado(true);
    try {
      const { data } = await api.post('/admin/marca-cajon/imagen', fd);
      aplicar(data);
      await cargarGaleria();
      toast.success('Imagen subida a la galería y puesta en la cabecera. Ajústala y guarda.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo subir la imagen');
    } finally {
      setOcupado(false);
      if (archivo.current) archivo.current.value = '';
    }
  };

  const guardar = async () => {
    setOcupado(true);
    try {
      const { data } = await api.put('/admin/marca-cajon', aj);
      setMarca(data);
      setSucio(false);
      toast.success('Guardado. La app lo mostrará al abrir el menú.');
    } catch {
      toast.error('No se pudo guardar');
    } finally {
      setOcupado(false);
    }
  };

  const quitar = async () => {
    if (!window.confirm('¿Quitar la imagen de la cabecera del menú de la app?')) return;
    setOcupado(true);
    try {
      const { data } = await api.delete('/admin/marca-cajon');
      aplicar(data);
      setGaleria((g) => g.map((x) => ({ ...x, activo: false })));
    } finally {
      setOcupado(false);
    }
  };

  // Colocación (idéntica a la app)
  const conImagen = !!marca?.activa;
  const H = aj.altura;
  const W = ANCHO_CAJON;
  const anchoImg = W * (aj.escala / 100);
  const altoImg = conImagen && marca.ancho ? anchoImg * (marca.alto / marca.ancho) : 0;
  const izq = (W - anchoImg) * (aj.x / 100);
  const arriba = (H - altoImg) * (aj.y / 100);
  const claro = textoClaro(aj, conImagen);

  // Arrastrar la imagen en la vista previa para colocarla; rueda = tamaño
  const bajar = (e) => {
    if (!conImagen) return;
    arrastre.current = { x: e.clientX, y: e.clientY, ax: aj.x, ay: aj.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const mover = (e) => {
    const a = arrastre.current;
    if (!a) return;
    const libreX = W - anchoImg;
    const libreY = H - altoImg;
    const dx = (e.clientX - a.x) / ESCALA_VISTA;
    const dy = (e.clientY - a.y) / ESCALA_VISTA;
    const nx = libreX !== 0 ? a.ax + (dx / libreX) * 100 : a.ax;
    const ny = libreY !== 0 ? a.ay + (dy / libreY) * 100 : a.ay;
    setAj((v) => ({ ...v, x: Math.max(0, Math.min(100, nx)), y: Math.max(0, Math.min(100, ny)) }));
    setSucio(true);
  };
  const soltar = () => { arrastre.current = null; };
  // Rueda = tamaño. Listener nativo (no pasivo): así la rueda no desplaza también la página
  const cabecera = useRef(null);
  useEffect(() => {
    const el = cabecera.current;
    if (!el || !conImagen) return undefined;
    const f = (e) => {
      e.preventDefault();
      setAj((a) => ({ ...a, escala: Math.max(20, Math.min(400, a.escala - Math.sign(e.deltaY) * 5)) }));
      setSucio(true);
    };
    el.addEventListener('wheel', f, { passive: false });
    return () => el.removeEventListener('wheel', f);
  }, [conImagen]);

  const nombre = user?.name || (user?.email || '').split('@')[0];
  const iniciales = (nombre || '?').split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  const colorTexto = claro ? '#fff' : '#0e1116';
  const colorTexto2 = claro ? 'rgba(255,255,255,.78)' : '#5b6270';

  return (
    <div className="grid gap-5 lg:grid-cols-[auto_minmax(0,1fr)]" data-testid="panel-marca-app">
      {/* Vista previa: el teléfono con el menú lateral abierto */}
      <div className="flex flex-col items-center gap-2">
        <div style={{ width: (ANCHO_TEL + 20) * ESCALA_VISTA, height: 800 * ESCALA_VISTA }}>
        <div style={{ width: ANCHO_TEL, height: 780, borderRadius: 44, border: '10px solid #111', background: '#f6f7f9', position: 'relative', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,.25)', transform: `scale(${ESCALA_VISTA})`, transformOrigin: 'top left' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.35)' }} />
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: W, background: '#fff' }}>
            <div
              onPointerDown={bajar}
              onPointerMove={mover}
              onPointerUp={soltar}
              onPointerCancel={soltar}
              ref={cabecera}
              data-testid="marca-preview-cabecera"
              style={{ position: 'relative', height: H, overflow: 'hidden', background: aj.fondo, cursor: conImagen ? 'grab' : 'default', touchAction: 'none' }}
            >
              {conImagen && (
                <img
                  src={`${base}${marca.imagen_url}`}
                  alt=""
                  draggable={false}
                  style={{ position: 'absolute', left: izq, top: arriba, width: anchoImg, height: altoImg, maxWidth: 'none', opacity: aj.opacidad / 100, filter: aj.desenfoque ? `blur(${aj.desenfoque}px)` : undefined, userSelect: 'none', pointerEvents: 'none' }}
                />
              )}
              {conImagen && <div style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${aj.oscurecer / 100})` }} />}
              <div style={{ position: 'absolute', left: 20, right: 20, bottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 17, flex: 'none', boxShadow: '0 0 0 2px rgba(255,255,255,.6)' }}>{iniciales}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: colorTexto, fontWeight: 600, fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nombre}</div>
                  <div style={{ color: colorTexto2, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</div>
                </div>
              </div>
            </div>
            {/* el resto del menú, para ver el conjunto */}
            <div style={{ padding: '8px 12px', fontSize: 14, color: '#0e1116' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 9.5, letterSpacing: '.08em', color: '#8a909c', margin: '4px 8px 6px' }}>CORREO</div>
              {['Recibidos', 'Destacados', 'En espera', 'Enviados', 'Borradores', 'Todos', 'Spam', 'Papelera'].map((t, i) => (
                <div key={t} style={{ padding: '7px 10px', borderRadius: 8, background: i === 0 ? '#e7f0fe' : 'transparent', color: i === 0 ? '#2563eb' : undefined, fontWeight: i === 0 ? 600 : 500 }}>{t}</div>
              ))}
              <div style={{ fontFamily: 'monospace', fontSize: 9.5, letterSpacing: '.08em', color: '#8a909c', margin: '10px 8px 6px' }}>APLICACIONES</div>
              {['Calendario', 'Meet', 'Drive'].map((t) => <div key={t} style={{ padding: '7px 10px' }}>{t}</div>)}
            </div>
          </div>
        </div>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Move className="h-3.5 w-3.5" /> Arrastra la imagen para colocarla · rueda para el tamaño</p>
      </div>

      <div className="space-y-5 min-w-0">
      {/* Galería: fondos de fábrica y los que se suban */}
      <Card className="p-5" data-testid="marca-galeria">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-medium">Galería de fondos</div>
            <p className="text-sm text-muted-foreground mt-1">Elige uno para la cabecera o sube los tuyos: se quedan aquí para volver a usarlos.</p>
          </div>
        </div>
        <div className="grid gap-3 mt-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {galeria.map((f) => (
            <div key={f.id} className="group relative">
              <button
                type="button"
                onClick={() => usar(f)}
                disabled={!!eligiendo}
                title={`Usar «${f.nombre}»`}
                data-testid="marca-galeria-fondo"
                className="block w-full text-left rounded-xl overflow-hidden border transition-shadow hover:shadow-md focus-visible:ring-2"
                style={{ borderColor: f.activo ? 'var(--accent)' : undefined, boxShadow: f.activo ? '0 0 0 2px var(--accent)' : undefined }}
              >
                <div className="relative" style={{ aspectRatio: '2 / 1', background: '#e5e7eb' }}>
                  <img src={`${base}${f.mini_url}`} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  {f.activo && <span className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full grid place-items-center" style={{ background: 'var(--accent)', color: '#fff' }}><Check className="h-4 w-4" /></span>}
                  {eligiendo === f.id && <span className="absolute inset-0 grid place-items-center bg-black/30"><Loader2 className="h-5 w-5 animate-spin text-white" /></span>}
                </div>
                <div className="px-2.5 py-1.5 text-sm truncate">{f.nombre}{!f.sistema && <span className="text-muted-foreground"> · subida</span>}</div>
              </button>
              {!f.sistema && (
                <button type="button" onClick={() => borrarDeGaleria(f)} title="Borrar de la galería" aria-label={`Borrar ${f.nombre}`}
                  className="absolute top-1.5 left-1.5 h-7 w-7 rounded-full grid place-items-center bg-white/90 text-destructive opacity-0 group-hover:opacity-100 focus:opacity-100 shadow">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => archivo.current?.click()} disabled={ocupado} data-testid="marca-galeria-subir"
            className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
            style={{ aspectRatio: '2 / 1.28' }}>
            {ocupado ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
            Subir imagen
          </button>
        </div>
      </Card>

      {/* Controles */}
      <Card className="p-5 space-y-4 self-start">
        <div>
          <div className="font-medium">Cabecera del menú de la app</div>
          <p className="text-sm text-muted-foreground mt-1">
            El logo o la imagen que se ve detrás de la foto, el nombre y el correo en el menú lateral de la app Orvian Mail.
            Es igual para toda la organización. PNG con transparencia, JPG o WebP de hasta 8 MB.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={archivo} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => subir(e.target.files?.[0])} data-testid="marca-archivo" />
          <Button className="gap-1.5" onClick={() => archivo.current?.click()} disabled={ocupado} data-testid="marca-subir"><ImagePlus className="h-4 w-4" />{conImagen ? 'Cambiar imagen' : 'Subir imagen'}</Button>
          {conImagen && <Button variant="outline" className="gap-1.5 text-destructive" onClick={quitar} disabled={ocupado} data-testid="marca-quitar"><Trash2 className="h-4 w-4" />Quitar</Button>}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Deslizador etiqueta="Tamaño" valor={aj.escala} min={20} max={400} sufijo=" %" onChange={(v) => cambiar('escala', v)} />
          <Deslizador etiqueta="Alto de la cabecera" valor={aj.altura} min={110} max={280} sufijo=" px" onChange={(v) => cambiar('altura', v)} />
          <Deslizador etiqueta="Posición horizontal" valor={aj.x} min={0} max={100} sufijo=" %" onChange={(v) => cambiar('x', v)} />
          <Deslizador etiqueta="Posición vertical" valor={aj.y} min={0} max={100} sufijo=" %" onChange={(v) => cambiar('y', v)} />
          <Deslizador etiqueta="Opacidad" valor={aj.opacidad} min={0} max={100} sufijo=" %" onChange={(v) => cambiar('opacidad', v)} />
          <Deslizador etiqueta="Oscurecer (para leer el texto)" valor={aj.oscurecer} min={0} max={85} sufijo=" %" onChange={(v) => cambiar('oscurecer', v)} />
          <Deslizador etiqueta="Desenfoque" valor={aj.desenfoque} min={0} max={20} sufijo=" px" onChange={(v) => cambiar('desenfoque', v)} />
          <label className="block text-sm">
            <span className="font-medium">Color de fondo</span>
            <span className="flex items-center gap-2 mt-1.5">
              <input type="color" value={aj.fondo} onChange={(e) => cambiar('fondo', e.target.value)} className="h-9 w-14 rounded border cursor-pointer" />
              <span className="text-muted-foreground">{aj.fondo}</span>
            </span>
          </label>
        </div>

        <div className="text-sm">
          <span className="font-medium">Color del texto</span>
          <div className="flex gap-2 mt-1.5">
            {[['auto', 'Automático'], ['claro', 'Claro'], ['oscuro', 'Oscuro']].map(([v, t]) => (
              <button key={v} type="button" onClick={() => cambiar('texto', v)} className={`px-3 py-1.5 rounded-lg border text-sm ${aj.texto === v ? 'bg-foreground text-background border-foreground' : ''}`}>{t}</button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button className="gap-1.5" onClick={guardar} disabled={ocupado || !sucio} data-testid="marca-guardar"><Save className="h-4 w-4" />Guardar</Button>
          <Button variant="outline" className="gap-1.5" onClick={() => { setAj({ ...DEFECTO }); setSucio(true); }} disabled={ocupado}><RotateCcw className="h-4 w-4" />Valores por defecto</Button>
          {sucio && <span className="text-sm text-muted-foreground self-center">Cambios sin guardar</span>}
        </div>
      </Card>
      </div>
    </div>
  );
}
